import React from "react";
import { useHistory } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { fetchDashboardData, fetchExpiringSoonTenants } from "action/dashboardAction";
import { dateUtils } from "utils/commonUtils";
import { useTranslation } from "react-i18next";

/* ── Icons ── */
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevronRightSmIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

function TenantMark({ name, index }) {
  const colors = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6"];
  const bg = colors[index % colors.length];
  const initials = (name || "T").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="lms-tenant-mark" style={{ background: bg }}>{initials}</div>
  );
}

function Avatar({ name }) {
  const initials = (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6"];
  const bg = colors[(name || "U").charCodeAt(0) % colors.length];
  return <div className="lms-avatar lms-avatar-sm" style={{ background: bg }}>{initials}</div>;
}

function Badge({ variant, children }) {
  return <span className={`lms-badge lms-badge-${variant}`}>{children}</span>;
}

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Dashboard() {
  React.useEffect(() => { document.title = "Dashboard | LMS Tenant"; }, []);
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dashboard, setDashboard] = React.useState(null);
  const [expiring, setExpiring] = React.useState({ data: [], meta: { current_page: 1, per_page: 5, total: 0, last_page: 1 } });
  const [expLoading, setExpLoading] = React.useState(false);
  const history = useHistory();

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchDashboardData();
        if (result.success) { setDashboard(result.data); setError(""); }
        else { setError(result.message || "Failed to load dashboard"); }
      } catch { setError("Failed to load dashboard"); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const loadExpiring = React.useCallback(async (page = 1) => {
    try {
      setExpLoading(true);
      const res = await fetchExpiringSoonTenants({ page, per_page: 5 });
      if (res.success) setExpiring({ data: res.data || [], meta: res.meta || {} });
    } catch {} finally { setExpLoading(false); }
  }, []);

  React.useEffect(() => { loadExpiring(1); }, [loadExpiring]);

  /* ── Derived data ── */
  const totalTenants = dashboard?.card?.total_tenants?.count || 0;
  const activeTenants = dashboard?.card?.total_tenants?.active_tenant || 0;
  const totalUsers = dashboard?.card?.total_users?.count || 0;
  const activeUsers = dashboard?.card?.total_users?.active_user || 0;
  const activeSubscriptions = dashboard?.card?.active_subscription?.count || 0;
  const expiredSubscriptions = dashboard?.card?.expired_subscription || 0;
  const tenantThisMonth = dashboard?.tenant_overview?.current_month_total_tenant || 0;
  const studentThisMonth = dashboard?.student_overview?.current_month_total_student || 0;

  const tenantMonths = (dashboard?.tenant_overview?.tenant_all_month || []).map(Number);
  const studentMonths = (dashboard?.student_overview?.student_all_month || []).map(Number);

  const recentTenants = (dashboard?.recent_tenants || []).slice(0, 5).map((tn) => ({
    ...tn,
    effectiveStatus: (tn.status || "").toLowerCase() === "active" ? "Active" : "Non Active",
    subscriptionPlan: tn.package || "Basic",
  }));
  const recentUsers = (dashboard?.recent_users || []).slice(0, 5).map((u) => ({
    id: u.id,
    nama: u.full_name || u.username || "-",
    email: u.email,
    role: "User",
    status: u.status_is_active ? "Active" : "Non Active",
  }));
  const expiringList = (expiring?.data || []).map((s) => ({
    id: s.id,
    tenant: s.name,
    package: s.package_name || "Basic",
    startDate: dateUtils.formatIndonesian(s.subscription_start_at),
    expiredDate: dateUtils.formatIndonesian(s.subscription_expired_at),
    gracePeriodDays: s.grace_period_days,
  }));

  /* ── Chart options ── */
  const lineOptions = {
    chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.32, opacityTo: 0, stops: [0, 100] } },
    colors: ["#4F46E5"],
    xaxis: { categories: MONTH_LABELS, labels: { style: { fontSize: "11px", colors: "#94A3B8" } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: "10px", colors: "#94A3B8" } } },
    grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
    dataLabels: { enabled: false },
    tooltip: { theme: "light" },
    markers: { size: 3, strokeWidth: 2, strokeColors: "#fff", colors: ["#4F46E5"] },
  };

  const barOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
    colors: ["#4F46E5"],
    xaxis: { categories: MONTH_LABELS, labels: { style: { fontSize: "11px", colors: "#94A3B8" } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: "10px", colors: "#94A3B8" } } },
    grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
    dataLabels: { enabled: false },
    tooltip: { theme: "light" },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 1, opacityTo: 0.7 } },
  };

  if (loading) {
    return (
      <div className="lms-page lms-anim-fade">
        <div className="lms-stat-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 120, background: "#F1F5F9", borderRadius: 16, animation: "lms-fadeIn 0.5s ease both", animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
        <div className="lms-dash-grid" style={{ marginTop: 24 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 280, background: "#F1F5F9", borderRadius: 16 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>Apps</span><ChevronRightIcon /><span>Admin</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Dashboard")}</span>
          </div>
          <div className="t-display">{t("Dashboard")}</div>
          <div className="t-small">{t("Selamat datang kembali. Berikut ringkasan platform hari ini.")}</div>
        </div>
        <button className="lms-btn lms-btn-primary" onClick={() => history.push("/admin/create-tenant")}>
          <PlusIcon /> {t("New Tenant")}
        </button>
      </div>

      {error && (
        <div className="lms-notice lms-notice-warning lms-mb-4">
          <AlertIcon />
          <span>{error}</span>
        </div>
      )}

      {/* Stat cards */}
      <div className="lms-stat-grid">
        <div className="lms-stat lms-anim-slide" style={{ animationDelay: "0ms" }} onClick={() => history.push("/admin/master-tenant")}>
          <div className="lms-stat-head">
            <span className="lms-stat-label">{t("Total Tenants")}</span>
            <span className="lms-stat-icon brand"><BuildingIcon /></span>
          </div>
          <div className="lms-stat-value">{totalTenants}</div>
          <div className="lms-stat-foot">
            <span className="lms-stat-trend up">↑</span>
            <span>{activeTenants} {t("active tenants")}</span>
          </div>
        </div>

        <div className="lms-stat lms-anim-slide" style={{ animationDelay: "60ms" }}>
          <div className="lms-stat-head">
            <span className="lms-stat-label">{t("Total Users")}</span>
            <span className="lms-stat-icon info"><UsersIcon /></span>
          </div>
          <div className="lms-stat-value">{totalUsers}</div>
          <div className="lms-stat-foot">
            <span className="lms-stat-trend up">↑</span>
            <span>{activeUsers} {t("active users")}</span>
          </div>
        </div>

        <div className="lms-stat lms-anim-slide" style={{ animationDelay: "120ms" }} onClick={() => history.push("/admin/master-tenant?filter=active")}>
          <div className="lms-stat-head">
            <span className="lms-stat-label">{t("Active Subscriptions")}</span>
            <span className="lms-stat-icon success"><ActivityIcon /></span>
          </div>
          <div className="lms-stat-value">{activeSubscriptions}</div>
          <div className="lms-stat-foot"><span>{t("Subscriptions aktif")}</span></div>
        </div>

        <div className="lms-stat lms-anim-slide" style={{ animationDelay: "180ms" }} onClick={() => history.push("/admin/master-tenant?filter=expired")}>
          <div className="lms-stat-head">
            <span className="lms-stat-label">{t("Expired Soon")}</span>
            <span className="lms-stat-icon warning"><AlertIcon /></span>
          </div>
          <div className="lms-stat-value">{expiredSubscriptions}</div>
          <div className="lms-stat-foot"><span style={{ color: "var(--warning-600)" }}>{t("Needs attention")}</span></div>
        </div>
      </div>

      {/* Charts */}
      <div className="lms-dash-grid">
        <div className="lms-card">
          <div className="lms-card-header">
            <div>
              <div className="t-h2">{t("Subscription Overview")}</div>
              <div className="t-small lms-mt-1">
                <span style={{ fontWeight: 600, color: "var(--success-600)" }}>{tenantThisMonth}</span> {t("new subscription this month")}
              </div>
            </div>
          </div>
          <div className="lms-card-body" style={{ paddingTop: 8 }}>
            {tenantMonths.some((v) => v > 0) ? (
              <ReactApexChart
                type="area"
                height={220}
                series={[{ name: "Tenants", data: tenantMonths }]}
                options={lineOptions}
              />
            ) : (
              <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
                {t("No data available")}
              </div>
            )}
          </div>
        </div>

        <div className="lms-card">
          <div className="lms-card-header">
            <div>
              <div className="t-h2">{t("Student Overview")}</div>
              <div className="t-small lms-mt-1">
                <span style={{ fontWeight: 600, color: "var(--brand-500)" }}>{studentThisMonth}</span> {t("new students this month")}
              </div>
            </div>
          </div>
          <div className="lms-card-body" style={{ paddingTop: 8 }}>
            {studentMonths.some((v) => v > 0) ? (
              <ReactApexChart
                type="bar"
                height={220}
                series={[{ name: "Students", data: studentMonths }]}
                options={barOptions}
              />
            ) : (
              <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
                {t("No data available")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent tables */}
      <div className="lms-dash-grid lms-mt-4">
        <div className="lms-card">
          <div className="lms-card-header">
            <div className="t-h2">{t("Recent Tenants")}</div>
            <button className="lms-btn lms-btn-ghost lms-btn-sm" onClick={() => history.push("/admin/master-tenant")}>
              {t("View all")} <ArrowRightIcon />
            </button>
          </div>
          <div className="lms-table-wrap">
            <table className="lms-table">
              <thead>
                <tr>
                  <th>{t("Tenant")}</th>
                  <th>{t("Plan")}</th>
                  <th>{t("Status")}</th>
                  <th style={{ textAlign: "right" }}>{t("Students")}</th>
                </tr>
              </thead>
              <tbody>
                {recentTenants.length === 0 ? (
                  <tr><td colSpan={4}>
                    <div className="lms-empty" style={{ padding: "32px 24px" }}>
                      <div className="t-small">{t("No recent tenants")}</div>
                    </div>
                  </td></tr>
                ) : recentTenants.map((tn, i) => (
                  <tr key={tn.id || i}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <TenantMark name={tn.namaTenant} index={i} />
                        <div>
                          <div className="td-strong">{tn.namaTenant}</div>
                          <div className="t-caption">{tn.domainUnik}</div>
                        </div>
                      </div>
                    </td>
                    <td><Badge variant="brand">{tn.subscriptionPlan}</Badge></td>
                    <td><Badge variant={tn.effectiveStatus === "Active" ? "success" : "danger"}>{tn.effectiveStatus}</Badge></td>
                    <td style={{ textAlign: "right" }} className="td-strong">{tn.total_students ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lms-card">
          <div className="lms-card-header">
            <div className="t-h2">{t("Recent Users")}</div>
            <button className="lms-btn lms-btn-ghost lms-btn-sm" onClick={() => history.push("/admin/user-management")}>
              {t("View all")} <ArrowRightIcon />
            </button>
          </div>
          <div className="lms-table-wrap">
            <table className="lms-table">
              <thead>
                <tr>
                  <th>{t("User")}</th>
                  <th>{t("Role")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.length === 0 ? (
                  <tr><td colSpan={3}>
                    <div className="lms-empty" style={{ padding: "32px 24px" }}>
                      <div className="t-small">{t("No recent users")}</div>
                    </div>
                  </td></tr>
                ) : recentUsers.map((u, i) => (
                  <tr key={u.id || i}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <Avatar name={u.nama} />
                        <div>
                          <div className="td-strong">{u.nama}</div>
                          <div className="t-caption">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><Badge variant={u.role === "Admin" ? "brand" : "neutral"}>{u.role}</Badge></td>
                    <td><Badge variant={u.status === "Active" ? "success" : "danger"}>{u.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Expiring subscriptions */}
      <div className="lms-card lms-mt-4">
        <div className="lms-card-header">
          <div>
            <div className="t-h2">{t("Subscriptions Expiring Within 3 Months")}</div>
            <div className="t-small lms-mt-1">{t("Tenant yang perlu segera diperpanjang")}</div>
          </div>
          {expiringList.length > 0 && (
            <span className="lms-badge lms-badge-warning">{expiringList.length} need action</span>
          )}
        </div>
        <div className="lms-table-wrap">
          <table className="lms-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Tenant</th>
                <th>{t("Package")}</th>
                <th>{t("Start Date")}</th>
                <th>{t("Expired Date")}</th>
                <th>{t("Grace Period")}</th>
              </tr>
            </thead>
            <tbody>
              {expLoading ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32 }}>
                  <div className="lms-spinner" />
                </td></tr>
              ) : expiringList.length === 0 ? (
                <tr><td colSpan={6}>
                  <div className="lms-empty" style={{ padding: "32px 24px" }}>
                    <div className="t-small">{t("Tidak ada subscription yang akan berakhir")}</div>
                  </div>
                </td></tr>
              ) : expiringList.map((s, i) => (
                <tr key={s.id || i}>
                  <td className="lms-text-muted">
                    {((expiring.meta.current_page || 1) - 1) * (expiring.meta.per_page || 5) + i + 1}
                  </td>
                  <td className="td-strong">{s.tenant}</td>
                  <td><Badge variant="brand">{s.package}</Badge></td>
                  <td>{s.startDate}</td>
                  <td style={{ color: "var(--danger-600)", fontWeight: 500 }}>{s.expiredDate}</td>
                  <td>
                    {s.gracePeriodDays != null ? (
                      <Badge variant={s.gracePeriodDays > 30 ? "warning" : "danger"}>{s.gracePeriodDays} {t("days")}</Badge>
                    ) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="lms-pagination">
          <span className="lms-pagination-info">
            Page <strong>{expiring.meta.current_page || 1}</strong> of <strong>{expiring.meta.last_page || 1}</strong>
          </span>
          <div className="lms-pagination-controls">
            <button
              className="lms-btn lms-btn-secondary lms-btn-sm"
              disabled={(expiring.meta.current_page || 1) <= 1 || expLoading}
              onClick={() => loadExpiring((expiring.meta.current_page || 1) - 1)}
            >
              <ChevronLeftIcon />
            </button>
            <button
              className="lms-btn lms-btn-secondary lms-btn-sm"
              disabled={(expiring.meta.current_page || 1) >= (expiring.meta.last_page || 1) || expLoading}
              onClick={() => loadExpiring((expiring.meta.current_page || 1) + 1)}
            >
              <ChevronRightSmIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
